import React, { useState, useEffect, useMemo } from 'react';
import { 
  X, BarChart2, TrendingUp, Target, FileText, Activity, Layers, Download, 
  MapPin, Loader2, Calendar, AlertTriangle, ArrowUpDown, ChevronDown, ChevronUp, MessageSquare, Send, Sparkles, History, Settings
} from 'lucide-react';
import { 
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer, ComposedChart, Area, AreaChart, Cell
} from 'recharts';
import jsPDF from 'jspdf';
// Removed html2canvas - using native jsPDF vector rendering for charts

// Uses the new function created in Phase 1
import { getEnterpriseAnalyticsDataFunction, saveReductionTargetsFunction, queryEnterpriseDataFunction } from '../firebase';
import logger from '../utils/logger';
import AIAnalyticsPanel from './AIAnalyticsPanel';
import { TrendsChart, BreakdownChart, ProjectionsChart, CarbonChart } from './EnterpriseCharts';



const EnterpriseAnalytics = ({ isOpen, onClose, darkMode }) => {
  const [activeTab, setActiveTab] = useState('trends'); // trends, variance, projections, targets, report
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Data State
  const [facilityData, setFacilityData] = useState({});
  const [facilityNames, setFacilityNames] = useState([]);
  const [allTargets, setAllTargets] = useState([]);
  
  // Controls State
  const [selectedFacility, setSelectedFacility] = useState('all');
  const [visibleFacilities, setVisibleFacilities] = useState([]); // List of site names to include when 'all' is selected
  const [timeRange, setTimeRange] = useState('3yr'); // 1yr, 2yr, 3yr, 5yr, all

  // Projection State
  const [whatIfReduction, setWhatIfReduction] = useState(0); // percentage

  // A3 Form State
  const [showFilters, setShowFilters] = useState(true);
  const [visibleCharts, setVisibleCharts] = useState(() => {
    const saved = localStorage.getItem('utilitrak_visible_charts');
    if (saved) return JSON.parse(saved);
    return ['trends', 'breakdown', 'projections', 'carbon'];
  });
  const [showDashboardCustomizer, setShowDashboardCustomizer] = useState(false);
  const [a3Form, setA3Form] = useState(() => {
    const saved = localStorage.getItem('utilitrak_a3_form');
    if (saved) return JSON.parse(saved);
    return {
      title: 'Utility Cost & Carbon Reduction Strategy',
      background: 'The enterprise portfolio currently lacks a unified strategy for utility optimization and carbon tracking.',
      currentCondition: 'Utility costs are rising above inflation; carbon metrics are not yet standardized across sites.',
      goals: 'Reduce overall utility costs by 15% and carbon emissions by 20% by end of next fiscal year.',
      rootCause: '1. Inefficient HVAC scheduling in warehouses\n2. Lack of sub-metering for high-energy processes\n3. Outdated lighting fixtures',
      countermeasures: '1. Implement automated setback thermostats\n2. Install sub-meters on production lines\n3. Complete LED retrofit',
      implementationPlan: 'Month 1: Thermostat config\nMonth 2-3: LED rollouts\nMonth 4: Sub-metering installation',
      followUp: 'Monthly review of utility variance dashboards and target tracking gauges.',
      includeTrendsChart: true,
      includeBreakdownChart: true,
      includeProjectionsChart: true,
      includeCarbonChart: true,
      includeCostSavings: true
    };
  });

  const [a3History, setA3History] = useState(() => {
    const saved = localStorage.getItem('utilitrak_a3_history');
    return saved ? JSON.parse(saved) : [];
  });
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    localStorage.setItem('utilitrak_a3_form', JSON.stringify(a3Form));
  }, [a3Form]);

  useEffect(() => {
    localStorage.setItem('utilitrak_visible_charts', JSON.stringify(visibleCharts));
  }, [visibleCharts]);

  useEffect(() => {
    localStorage.setItem('utilitrak_a3_history', JSON.stringify(a3History));
  }, [a3History]);

  // Target Form State
  const [targetForm, setTargetForm] = useState({
    facility: 'all',
    type: 'percentage', // percentage, absolute
    utility: 'carbon', // carbon, electric, gas
    value: 15,
    baselineYear: new Date().getFullYear() - 1
  });
  const [savingTarget, setSavingTarget] = useState(false);
  const [trendViewMode, setTrendViewMode] = useState('aggregated'); // aggregated, breakdown, comparison

  // AI State
  const [aiChatOpen, setAiChatOpen] = useState(false);

  useEffect(() => {
    if (isOpen && Object.keys(facilityData).length === 0) {
      loadData();
    }
  }, [isOpen]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getEnterpriseAnalyticsDataFunction({ selectedFacility: 'all' });
      setFacilityData(result.data.facilityData);
      setFacilityNames(result.data.facilityNames);
      setAllTargets(result.data.allTargets || []);
      
      // Initialize visible facilities if not set
      if (visibleFacilities.length === 0) {
        setVisibleFacilities(result.data.facilityNames.map(f => f.name));
      }
    } catch (err) {
      logger.error('Failed to load enterprise analytics:', err);
      setError(err.message || 'Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const [isAutoFilling, setIsAutoFilling] = useState(false);

  const handleAutoFillA3 = async () => {
    try {
      setIsAutoFilling(true);
      const dataPayload = {
        facilityScope: selectedFacility,
        timeSpan: timeRange,
        historicalContext: chartData,
        projections: projectionData,
        targets: allTargets.filter(t => t.facility === 'all' || t.facility === selectedFacility)
      };

      const prompt = `Perform a professional A3 Problem Solving analysis for the utility data of ${selectedFacility === 'all' ? 'the entire portfolio' : selectedFacility}. Focus on ${targetForm.utility === 'all' ? 'all utilities' : targetForm.utility}.
      
      Format your response as a JSON object with these keys: title, background, currentCondition, goals, rootCause, countermeasures, implementationPlan, followUp.
      
      CRITICAL FORMATTING INSTRUCTION: 
      - Use '# Section Title' for primary headers (H1 style).
      - Use '## Subsection' for secondary points (H2 style).
      - Use bullet points (•) for lists.
      - Keep responses concise and actionable.
      - NEVER use double-asterisks (**) for bolding. If you want to emphasize a title in a list, just use '• Title: Description'.
      
      Context data (last 12 months): ${JSON.stringify(dataPayload)}
      Existing Targets: ${JSON.stringify(targetForm)}`;

      const response = await queryEnterpriseDataFunction({ 
        question: prompt, 
        contextData: null 
      });
      
      const answer = response.data.answer || '';
      const cleaned = answer.replace(/```json/gi, '').replace(/```/gi, '').trim();
      const parsed = JSON.parse(cleaned);

      setA3History(prev => [{
        timestamp: new Date().toISOString(),
        data: { ...a3Form }
      }, ...prev].slice(0, 10));

      setA3Form(prev => ({
        ...prev,
        ...parsed
      }));
    } catch (err) {
      logger.error('Failed to auto-fill A3:', err);
      alert('AI failed to parse or generate response. Check console logs.');
    } finally {
      setIsAutoFilling(false);
    }
  };


  const handleSaveTarget = async (e) => {
    e.preventDefault();
    setSavingTarget(true);
    try {
      await saveReductionTargetsFunction({
        facility: targetForm.facility,
        targetType: targetForm.type,
        utilityTargeted: targetForm.utility,
        value: parseFloat(targetForm.value),
        baselineYear: targetForm.baselineYear.toString()
      });
      await loadData();
      alert('Reduction target saved successfully!');
    } catch (err) {
      logger.error('Failed to save target:', err);
      alert('Failed to save target. Please try again.');
    } finally {
      setSavingTarget(false);
    }
  };

  const tabs = [
    { id: 'trends', label: 'Historical Trends', icon: <TrendingUp className="h-4 w-4" /> },
    { id: 'variance', label: 'Variance Analysis', icon: <ArrowUpDown className="h-4 w-4" /> },
    { id: 'projections', label: 'Projections & Forecasting', icon: <BarChart2 className="h-4 w-4" /> },
    { id: 'targets', label: 'Targets & Allocation', icon: <Target className="h-4 w-4" /> },
    { id: 'report', label: 'A3 Report Generator', icon: <FileText className="h-4 w-4" /> }
  ];

  if (!isOpen) return null;

  // --- Chart Data Aggregation ---
  const chartData = useMemo(() => {
    if (Object.keys(facilityData).length === 0) return [];
    
    let facilitiesToAnalyze = [];
    if (selectedFacility === 'all') {
      facilitiesToAnalyze = Object.values(facilityData).filter(f => visibleFacilities.includes(f.name));
    } else {
      facilitiesToAnalyze = [facilityData[selectedFacility]].filter(Boolean);
    }

    const aggregated = {};
    facilitiesToAnalyze.forEach(f => {
      f.months?.forEach(m => {
        if (!aggregated[m.month]) {
          aggregated[m.month] = { 
            month: m.month, 
            total_elec_kwh: 0, 
            total_gas_ccf: 0, 
            total_cost: 0,
            carbon_emissions: 0 
          };
        }
        const eUsage = m.electric_kwh || m.electric_usage || 0;
        const gUsage = m.gas_ccf || m.gas_usage || 0;
        
        aggregated[m.month].total_elec_kwh += eUsage;
        aggregated[m.month].total_gas_ccf += gUsage;
        aggregated[m.month].total_cost += (m.total_cost || 0);
        aggregated[m.month].carbon_emissions += (m.carbon_emissions || 0);
        
        if (selectedFacility === 'all') {
            aggregated[m.month][`${f.name}_cost`] = (m.total_cost || 0);
            aggregated[m.month][`${f.name}_elec`] = eUsage;
            aggregated[m.month][`${f.name}_gas`] = gUsage;
            aggregated[m.month][`${f.name}_carbon`] = (m.carbon_emissions || 0);
        }
      });
    });

    let data = Object.values(aggregated).sort((a, b) => a.month.localeCompare(b.month));

    if (trendViewMode === 'comparison' && selectedFacility === 'all') {
        data.forEach(d => {
            d.org_total_cost = d.total_cost;
            d.org_total_elec = d.total_elec_kwh;
        });
    }

    if (timeRange !== 'all') {
      const monthsToKeep = 
        timeRange === '1yr' ? 12 :
        timeRange === '2yr' ? 24 :
        timeRange === '3yr' ? 36 :
        timeRange === '5yr' ? 60 : 36;
      data = data.slice(-monthsToKeep);
    }

    return data;
  }, [facilityData, selectedFacility, timeRange, visibleFacilities, trendViewMode]);

  // --- Variance Data Aggregation ---
  const varianceData = useMemo(() => {
    if (chartData.length < 12) return null;
    
    const sorted = [...chartData]; 
    const ltm = sorted.slice(-12);
    const ptm = sorted.slice(-24, -12);
    
    const sum = (arr, key) => arr.reduce((acc, curr) => acc + (curr[key] || 0), 0);
    
    const stats = {
      ltm: {
        cost: sum(ltm, 'total_cost'),
        elec: sum(ltm, 'total_elec_kwh'),
        gas: sum(ltm, 'total_gas_ccf'),
        carbon: sum(ltm, 'carbon_emissions')
      },
      ptm: {
        cost: sum(ptm, 'total_cost'),
        elec: sum(ptm, 'total_elec_kwh'),
        gas: sum(ptm, 'total_gas_ccf'),
        carbon: sum(ptm, 'carbon_emissions')
      }
    };

    const calcVar = (cur, prev) => prev === 0 ? 0 : ((cur - prev) / prev) * 100;
    
    stats.variance = {
      cost: calcVar(stats.ltm.cost, stats.ptm.cost),
      elec: calcVar(stats.ltm.elec, stats.ptm.elec),
      gas: calcVar(stats.ltm.gas, stats.ptm.gas),
      carbon: calcVar(stats.ltm.carbon, stats.ptm.carbon)
    };

    return stats;
  }, [chartData]);

  // --- Projection Data ---
  const projectionData = useMemo(() => {
    if (chartData.length < 12) return [];
    
    const ltm = chartData.slice(-12);
    const avg = {
      elec: ltm.reduce((acc, c) => acc + (c.total_elec_kwh || 0), 0) / 12,
      gas: ltm.reduce((acc, c) => acc + (c.total_gas_ccf || 0), 0) / 12,
      cost: ltm.reduce((acc, c) => acc + (c.total_cost || 0), 0) / 12,
      carbon: ltm.reduce((acc, c) => acc + (c.carbon_emissions || 0), 0) / 12,
    };
    
    const activeFacilityTargets = allTargets.filter(t => t.facility === selectedFacility);
    let targetCarbon = null;
    let targetCost = null;

    if (activeFacilityTargets.length > 0) {
        let maxPerc = 0;
        let cAbs = 0;
        
        activeFacilityTargets.forEach(t => {
            if (t.targetType === 'percentage') {
                if (t.value > maxPerc) maxPerc = t.value;
            } else if (t.utilityTargeted === 'carbon' && t.targetType === 'absolute') {
                cAbs += t.value / 12;
            }
        });
        
        if (maxPerc > 0) {
            targetCarbon = avg.carbon * (1 - (maxPerc/100));
            targetCost = avg.cost * (1 - (maxPerc/100));
        } else if (cAbs > 0) {
            targetCarbon = Math.max(0, avg.carbon - cAbs);
            targetCost = avg.cost * (targetCarbon / avg.carbon);
        }
    }

    const multiplier = 1 - (whatIfReduction / 100);
    const lastMonth = chartData[chartData.length - 1].month;
    let [year, month] = lastMonth.split('-').map(Number);
    
    const projections = [];
    for (let i = 1; i <= 12; i++) {
        month++;
        if (month > 12) { month = 1; year++; }
        const monthStr = `${year}-${month.toString().padStart(2, '0')}`;
        
        projections.push({
          month: monthStr,
          forecast_elec_kwh: avg.elec * multiplier,
          forecast_gas_ccf: avg.gas * multiplier,
          forecast_cost: avg.cost * multiplier,
          forecast_carbon: avg.carbon * multiplier,
          ...(targetCarbon !== null ? { target_carbon: targetCarbon, target_cost: targetCost } : {})
        });
    }
    
    return projections;
  }, [chartData, whatIfReduction, allTargets, selectedFacility]);

  const siteColors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#F97316', '#6366F1', '#14B8A6'];
  const isActiveSite = (name) => selectedFacility === 'all' ? visibleFacilities.includes(name) : selectedFacility === name;

  const combinedData = useMemo(() => {
    if (chartData.length === 0) return [];
    
    const ltm = chartData.slice(-12);
    const avg = {
      cost: ltm.reduce((acc, c) => acc + (c.total_cost || 0), 0) / 12,
      carbon: ltm.reduce((acc, c) => acc + (c.carbon_emissions || 0), 0) / 12,
    };

    const activeFacilityTargets = allTargets.filter(t => t.facility === selectedFacility);
    let targetCarbon = null;
    let targetCost = null;

    if (activeFacilityTargets.length > 0) {
      let maxPerc = 0;
      let cAbs = 0;
      activeFacilityTargets.forEach(t => {
        if (t.targetType === 'percentage') { if (t.value > maxPerc) maxPerc = t.value; }
        else if (t.utilityTargeted === 'carbon' && t.targetType === 'absolute') { cAbs += t.value / 12; }
      });
      if (maxPerc > 0) {
        targetCarbon = avg.carbon * (1 - (maxPerc/100));
        targetCost = avg.cost * (1 - (maxPerc/100));
      } else if (cAbs > 0) {
        targetCarbon = Math.max(0, avg.carbon - cAbs);
        targetCost = avg.cost * (targetCarbon / avg.carbon);
      }
    }

    const historicalTail = chartData.slice(-12).map(d => ({
      ...d, 
      isHistory: true,
      target_cost: targetCost,
      target_carbon: targetCarbon,
      target_status: targetCost ? (d.total_cost <= targetCost ? 'HIT' : 'MISS') : null
    }));

    const forecastTail = (projectionData || []).map(d => ({
      ...d, 
      isHistory: false,
      target_cost: targetCost,
      target_carbon: targetCarbon,
      potential_savings: avg.cost - (d.forecast_cost || 0)
    }));

    return [...historicalTail, ...forecastTail];
  }, [chartData, projectionData, allTargets, selectedFacility]);

  const renderTrendsChartBody = () => (
    <TrendsChart 
      data={chartData} 
      darkMode={darkMode} 
      trendViewMode={trendViewMode} 
      facilityData={facilityData} 
      selectedFacility={selectedFacility} 
      visibleFacilities={visibleFacilities} 
      siteColors={siteColors} 
      isActiveSite={isActiveSite} 
    />
  );

  const renderBreakdownChartBody = () => (
    <BreakdownChart 
      data={chartData} 
      darkMode={darkMode} 
      visibleFacilities={visibleFacilities} 
      facilityNames={facilityNames} 
      siteColors={siteColors} 
    />
  );

  const renderProjectionsChartBody = () => <ProjectionsChart data={combinedData} darkMode={darkMode} />;

  const renderCarbonChartBody = () => <CarbonChart data={chartData} darkMode={darkMode} />;

  const generateA3Report = async () => {
    try {
      setLoading(true);
      const doc = new jsPDF('landscape', 'pt', 'a3');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 40;
      const currentFacilityName = selectedFacility === 'all' ? 'Enterprise Portfolio' : selectedFacility;
      
      const brandColors = {
        primary: [59, 130, 246],    // Blue 500
        secondary: [16, 185, 129],  // Emerald 500
        accent: [245, 158, 11],     // Amber 500
        carbon: [139, 92, 246],     // Violet 500
        header: [30, 41, 59],       // Slate 800
        text: [30, 41, 59],         // Slate 800
        muted: [100, 116, 139],     // Slate 500
        boxHeader: [241, 245, 249],  // Slate 50
        border: [226, 232, 240]      // Slate 200
      };

      const drawNativeUtilityChart = (type, title, data, x, y, w, h) => {
        doc.setFillColor(252, 253, 254);
        doc.rect(x, y, w, h, 'F');
        doc.setDrawColor(...brandColors.border);
        doc.rect(x, y, w, h);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(...brandColors.header);
        doc.text(title.toUpperCase(), x + 15, y + 25);
        const chartMargin = 45;
        const cX = x + chartMargin;
        const cY = y + 45;
        const cW = w - (chartMargin * 2);
        const cH = h - 90;
        doc.setDrawColor(...brandColors.muted);
        doc.line(cX, cY + cH, cX + cW, cY + cH);
        doc.line(cX, cY, cX, cY + cH);
        if (!data || data.length === 0) return;
        const getMaxValue = (keys) => {
          let max = 0;
          data.forEach(d => keys.forEach(k => { if (d[k] > max) max = d[k]; }));
          return max * 1.15 || 100;
        };
        const drawYAxis = (max, isRight = false, color = brandColors.muted) => {
          const ticks = 4;
          const xPos = isRight ? cX + cW : cX;
          doc.setFontSize(7);
          doc.setTextColor(...color);
          for (let i = 0; i <= ticks; i++) {
            const val = (max / ticks) * i;
            const yPos = cY + cH - (cH * (i / ticks));
            doc.setDrawColor(...color);
            doc.line(xPos - 3, yPos, xPos + 3, yPos);
            doc.text(val.toLocaleString(undefined, { maximumFractionDigits: 0 }), xPos - (isRight ? -10 : 38), yPos + 2.5);
          }
        };
        const stepX = cW / data.length;
        const barW = stepX * 0.6;
        if (type === 'trends') {
          const maxUsage = getMaxValue(['total_elec_kwh', 'total_gas_ccf']);
          const maxCost = getMaxValue(['total_cost']);
          drawYAxis(maxUsage);
          drawYAxis(maxCost, true, brandColors.secondary);
          data.forEach((d, i) => {
            const curX = cX + (i * stepX) + (stepX - barW) / 2;
            const hElec = (d.total_elec_kwh / maxUsage) * cH;
            const hGas = (d.total_gas_ccf / maxUsage) * cH;
            doc.setFillColor(...brandColors.primary); doc.rect(curX, cY + cH - hElec, barW, hElec, 'F');
            doc.setFillColor(...brandColors.accent); doc.rect(curX, cY + cH - hElec - hGas, barW, hGas, 'F');
            if (i > 0) {
              const prevX = cX + ((i - 1) * stepX) + stepX / 2;
              const prevY = cY + cH - (data[i - 1].total_cost / maxCost) * cH;
              const nextY = cY + cH - (d.total_cost / maxCost) * cH;
              doc.setDrawColor(...brandColors.secondary); doc.setLineWidth(2);
              doc.line(prevX, prevY, cX + (i * stepX) + stepX / 2, nextY);
              doc.setLineWidth(1);
            }
            if (i % 3 === 0) { doc.setFontSize(6); doc.text(d.month.split('-')[1], curX, cY + cH + 12); }
          });
        } else if (type === 'breakdown') {
          const maxCost = getMaxValue(['total_cost']);
          drawYAxis(maxCost);
          data.forEach((d, i) => {
            const curX = cX + (i * stepX) + (stepX - barW) / 2;
            let currentStackH = 0;
            facilityNames.filter(f => visibleFacilities.includes(f.name)).forEach((f, idx) => {
              const val = d[`${f.name}_cost`] || 0;
              const hBar = (val / maxCost) * cH;
              const color = siteColors[idx % siteColors.length].replace('#', '');
              const rgb = [parseInt(color.substring(0,2),16), parseInt(color.substring(2,4),16), parseInt(color.substring(4,6),16)];
              doc.setFillColor(...rgb);
              doc.rect(curX, cY + cH - currentStackH - hBar, barW, hBar, 'F');
              currentStackH += hBar;
            });
            if (i % 3 === 0) { doc.setFontSize(6); doc.text(d.month.split('-')[1], curX, cY + cH + 12); }
          });
        } else if (type === 'projections') {
           const maxVal = getMaxValue(['total_cost', 'forecast_cost', 'target_cost']);
           drawYAxis(maxVal);
           data.forEach((d, i) => {
             const curX = cX + (i * stepX) + stepX / 2;
             if (i > 0) {
               const prevX = cX + ((i - 1) * stepX) + stepX / 2;
               const prevY = cY + cH - ((data[i-1].total_cost || data[i-1].forecast_cost) / maxVal) * cH;
               const nextY = cY + cH - ((d.total_cost || d.forecast_cost) / maxVal) * cH;
               
               if (d.isHistory) {
                  doc.setDrawColor(...brandColors.carbon); doc.setLineWidth(2);
                  doc.line(prevX, prevY, curX, nextY);
               } else {
                  doc.setDrawColor(...brandColors.primary); doc.setLineWidth(2);
                  for (let j = 0; j < 5; j++) {
                     const fx = prevX + (curX-prevX)*(j/5);
                     const fy = prevY + (nextY-prevY)*(j/5);
                     const tx = prevX + (curX-prevX)*((j+0.5)/5);
                     const ty = prevY + (nextY-prevY)*((j+0.5)/5);
                     doc.line(fx, fy, tx, ty);
                  }
               }
               doc.setLineWidth(1);
             }
             if (i % 3 === 0) { doc.setFontSize(6); doc.text(d.month.split('-')[1], curX, cY + cH + 12); }
           });
           doc.setFont('helvetica', 'italic'); doc.setFontSize(7); doc.setTextColor(...brandColors.muted);
           doc.text("* Solid line: Historical Trends | Dashed line: Forecasted Projections", cX, cY + cH + 25);
        } else {
           const maxVal = getMaxValue(['carbon_emissions']);
           drawYAxis(maxVal);
           data.forEach((d, i) => {
             const curX = cX + (i * stepX) + stepX / 2;
             if (i > 0) {
               const prevX = cX + ((i - 1) * stepX) + stepX / 2;
               const prevY = cY + cH - (data[i-1].carbon_emissions / maxVal) * cH;
               const nextY = cY + cH - (d.carbon_emissions / maxVal) * cH;
               doc.setDrawColor(...brandColors.carbon); doc.setLineWidth(2);
               doc.line(prevX, prevY, curX, nextY);
               doc.setLineWidth(1);
             }
             if (i % 3 === 0) { doc.setFontSize(6); doc.text(d.month.split('-')[1], curX, cY + cH + 12); }
           });
        }
      };

      const addHeaderFooter = (doc, pageTitle, facility) => {
        const pNum = doc.internal.getNumberOfPages();
        doc.setPage(pNum);
        doc.setFillColor(...brandColors.header);
        doc.rect(0, 0, pageWidth, 70, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(22);
        doc.text("UTILI-TRACK | " + pageTitle, margin, 45);
        doc.setFontSize(10);
        doc.text(`Facility: ${facility}`, pageWidth - margin - 200, 35, { align: 'right' });
        doc.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth - margin - 200, 50, { align: 'right' });
        doc.setDrawColor(...brandColors.border);
        doc.line(margin, pageHeight - 40, pageWidth - margin, pageHeight - 40);
        doc.setTextColor(...brandColors.muted);
        doc.setFontSize(9);
        doc.text("© 2024 UtiliTrack Cloud Solutions. All Rights Reserved.", margin, pageHeight - 25);
        doc.text(`Page ${pNum}`, pageWidth - margin - 30, pageHeight - 25);
      };

      const drawBox = (title, content, x, y, w, h, isBlue = false) => {
        const primaryColor = isBlue ? [248, 250, 252] : [255, 255, 255]; // Slate 50 or White
        const secondaryColor = brandColors.boxHeader;
        doc.setFillColor(...secondaryColor);
        doc.rect(x, y, w, h, 'F');
        doc.setFillColor(...brandColors.boxHeader);
        doc.rect(x, y, w, 15, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8);
        doc.setTextColor(...brandColors.header);
        doc.text(title.toUpperCase(), x + 6, y + 10);
        doc.setFillColor(...primaryColor);
        doc.rect(x, y + 15, w, h - 15, 'F');
        const lines = (content || '').split('\n');
        let currentY = y + 25;
        let didOverflow = false;
        lines.forEach(line => {
          if (currentY > y + h - 15) {
            if (!didOverflow) { doc.setFont('helvetica', 'italic'); doc.setFontSize(7); doc.text("(See detailed narrative on page 2...)", x + 5, y + h - 5); didOverflow = true; }
            return;
          }
          const isH1 = line.startsWith('# ');
          const isH2 = line.startsWith('## ') || line.startsWith('**');
          if (isH1) {
            doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(...brandColors.primary);
            const text = line.replace('# ', '');
            const split = doc.splitTextToSize(text, w - 10);
            doc.text(split, x + 5, currentY); currentY += (split.length * 13);
          } else if (isH2) {
            doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(...brandColors.muted);
            const text = line.replace('## ', '').replace(/\*\*/g, '');
            const split = doc.splitTextToSize(text, w - 10);
            doc.text(split, x + 5, currentY); currentY += (split.length * 11);
          } else {
            doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.setTextColor(...brandColors.text);
            const cleanLine = line.replace(/\*\*/g, '');
            const split = doc.splitTextToSize(cleanLine, w - 10);
            doc.text(split, x + 5, currentY); currentY += (split.length * 10);
          }
        });
        doc.setDrawColor(...brandColors.border); doc.rect(x, y, w, h);
      };

      const drawFullNarrative = (title, content, startY) => {
        doc.setFont('helvetica', 'bold'); doc.setFontSize(14); doc.setTextColor(...brandColors.header);
        doc.text(title, margin, startY);
        const lines = (content || '').split('\n');
        let curY = startY + 20;
        lines.forEach(line => {
          if (curY > pageHeight - 60) { doc.addPage(); addHeaderFooter(doc, 'Detailed Strategy (Cont.)', currentFacilityName); curY = 100; }
          const isH1 = line.startsWith('# ');
          const isH2 = line.startsWith('## ') || line.startsWith('**');
          if (isH1) {
            doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor(...brandColors.primary);
            const text = line.replace('# ', '');
            const split = doc.splitTextToSize(text, pageWidth - margin * 2);
            doc.text(split, margin, curY); curY += (split.length * 16);
          } else if (isH2) {
            doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(...brandColors.muted);
            const text = line.replace('## ', '').replace(/\*\*/g, '');
            const split = doc.splitTextToSize(text, pageWidth - margin * 2);
            doc.text(split, margin, curY); curY += (split.length * 14);
          } else {
            doc.setFont('helvetica', 'normal'); doc.setFontSize(10.5); doc.setTextColor(...brandColors.text);
            const cleanLine = line.replace(/\*\*/g, '');
            const split = doc.splitTextToSize(cleanLine, pageWidth - margin * 2);
            doc.text(split, margin, curY); curY += (split.length * 13);
          }
        });
        return curY + 30;
      };

      // PAGE 1: Summary Grid
      const colW = (pageWidth - (margin * 3)) / 2;
      addHeaderFooter(doc, `A3 PROBLEM SOLVING: ${a3Form.title}`, currentFacilityName);
      drawBox('1. Background', a3Form.background, margin, 100, colW, 200);
      drawBox('2. Current Condition', a3Form.currentCondition, margin + colW + margin, 100, colW, 200, true);
      drawBox('3. Goals', a3Form.goals, margin, 310, colW, 140);
      drawBox('4. Root Cause', a3Form.rootCause, margin + colW + margin, 310, colW, 140);
      drawBox('5. Countermeasures', a3Form.countermeasures, margin, 460, colW, 200, true);
      drawBox('6. Implementation', a3Form.implementationPlan, margin + colW + margin, 460, colW, 200);
      drawBox('7. Follow-up', a3Form.followUp, margin, 670, colW * 2 + margin, 100, true);

      // PAGE 2+: Detailed Narrative Extension
      doc.addPage();
      addHeaderFooter(doc, 'Strategic Execution Narrative', currentFacilityName);
      let nY = 100;
      nY = drawFullNarrative('Expanded Background & Context', a3Form.background, nY);
      nY = drawFullNarrative('Current State Analysis', a3Form.currentCondition, nY);
      nY = drawFullNarrative('Root Cause Breakdown', a3Form.rootCause, nY);
      nY = drawFullNarrative('Proposed Countermeasures', a3Form.countermeasures, nY);
      nY = drawFullNarrative('Implementation Roadmap', a3Form.implementationPlan, nY);
      nY = drawFullNarrative('Sustaining Gains & Follow-up', a3Form.followUp, nY);

      // 4. APPENDIX: High-Res Full Page Charts
      const selectedCharts = [
        { id: 'a3-trends-chart', title: 'Historical Usage & Cost Trends', active: a3Form.includeTrendsChart, caption: 'Figure 1.1: Multi-year trajectory of utility consumption vs. pricing.' },
        { id: 'a3-breakdown-chart', title: 'Portfolio Contribution Breakdown', active: a3Form.includeBreakdownChart, caption: 'Figure 2.1: Percentage of total enterprise spending attributed to individual sites.' },
        { id: 'a3-projections-chart', title: 'Forecasting & Target Projections', active: a3Form.includeProjectionsChart, caption: 'Figure 3.1: Expected outcomes assuming implementation of optimization measures.' },
        { id: 'a3-carbon-chart', title: 'Carbon Emissions Profile', active: a3Form.includeCarbonChart, caption: 'Figure 4.1: Historical and projected environmental footprint in metric tons CO2e.' }
      ].filter(c => c.active);

        for (let i = 0; i < selectedCharts.length; i++) {
          const chart = selectedCharts[i];
          doc.addPage();
          addHeaderFooter(doc, 'Visual Analysis Gap', currentFacilityName);
          
          let chartType = 'trends';
          let chartDataSource = chartData;
          if (chart.id === 'a3-breakdown-chart') chartType = 'breakdown';
          if (chart.id === 'a3-projections-chart') { chartType = 'projections'; chartDataSource = combinedData; }
          if (chart.id === 'a3-carbon-chart') chartType = 'carbon';

          drawNativeUtilityChart(chartType, chart.title, chartDataSource, margin, 120, pageWidth - margin * 2, 450);
          
          doc.setFont('helvetica', 'bold italic');
          doc.setFontSize(11);
          doc.setTextColor(...brandColors.muted);
          doc.text(`Description: ${chart.caption}`, margin, 595);
        }

      // 5. APPENDIX: Financial Impact Analysis
      const activeTargets = allTargets.filter(t => t.facility === 'all' || t.facility === selectedFacility);
      if (a3Form.includeCostSavings && activeTargets.length > 0) {
        doc.addPage();
        addHeaderFooter(doc, 'Strategic Cost Analysis', currentFacilityName);
        
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(22);
        doc.setTextColor(31, 41, 55);
        doc.text('Reduction Target Verification & Potential Financial Impact', margin, 120);
        
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(12);
        doc.setTextColor(107, 114, 128);
        doc.text('Strategic modeling based on current utility rates and confirmed reduction targets for the next fiscal cycle.', margin, 145);

        let curY_fin = 190;
        doc.setFillColor(...brandColors.boxHeader);
        doc.rect(margin, curY_fin, pageWidth - (margin * 2), 35, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(...brandColors.header);
        doc.text('UTILITY / TARGET SCOPE', margin + 10, curY_fin + 22);
        doc.text('LTM BASELINE AVG', margin + 250, curY_fin + 22);
        doc.text('ANNUAL GOAL REDUCTION', margin + 550, curY_fin + 22);
        doc.text('EST. ANNUAL SAVINGS ($)', margin + 850, curY_fin + 22);
        curY_fin += 35;

        activeTargets.forEach(target => {
          doc.setFont('helvetica', 'normal');
          doc.setDrawColor(...brandColors.border);
          doc.line(margin, curY_fin, pageWidth - margin, curY_fin);
          
          const key = target.utilityTargeted === 'carbon' ? 'carbon' : target.utilityTargeted === 'electric' ? 'elec' : 'gas';
          const unit = target.utilityTargeted === 'carbon' ? 'mt' : target.utilityTargeted === 'electric' ? 'kWh' : 'CCF';
          const baseline = varianceData ? (varianceData.ltm[key] || 0) : 0;
          const monthlyBaseline = baseline / 12;
          const reductionAmt = target.targetType === 'percentage' ? (monthlyBaseline * (target.value / 100)) : (target.value / 12);
          
          let financial = 0;
          if (target.utilityTargeted === 'electric') financial = reductionAmt * 12 * 0.12; 
          if (target.utilityTargeted === 'gas') financial = reductionAmt * 12 * 1.5;
          if (target.utilityTargeted === 'carbon') financial = reductionAmt * 12 * 25;

          doc.setFontSize(10.5);
          doc.setTextColor(...brandColors.text);
          doc.text(`${target.value}${target.targetType === 'percentage' ? '%' : ''} ${target.utilityTargeted.toUpperCase()} (${target.facility})`, margin + 10, curY_fin + 24);
          doc.text(`${monthlyBaseline.toLocaleString(undefined, {maximumFractionDigits: 0})} ${unit}/mo`, margin + 250, curY_fin + 24);
          doc.text(`-${(reductionAmt * 12).toLocaleString(undefined, {maximumFractionDigits: 0})} ${unit}/yr`, margin + 550, curY_fin + 24);
          
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(22, 163, 74);
          doc.text(`+$${financial.toLocaleString(undefined, {maximumFractionDigits: 0})}`, margin + 850, curY_fin + 24);
          doc.setTextColor(...brandColors.text);
          curY_fin += 35;
        });

        doc.setFillColor(...brandColors.boxHeader);
        doc.rect(margin, curY_fin, pageWidth - (margin * 2), 50, 'F');
        doc.setFontSize(10);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(...brandColors.muted);
        doc.text('* Financial impact is based on national average rates ($0.12/kWh, $1.50/CCF) and carbon tax proxy of $25/mt.', margin + 10, curY_fin + 30);
      }
      doc.save(`A3_DETAILED_REPORT_${new Date().getTime()}.pdf`);
    } catch (err) {
      logger.error('Failed to generate A3 Report:', err);
      alert('Error generating PDF. Check console.');
    } finally {
      setLoading(false);
    }
  };

  // --- Scaffolding components for tabs ---
  const renderTrendsTab = () => {
    // Generate distinct colors for sites
    const siteColors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#F97316', '#6366F1', '#14B8A6'];

    const isActiveSite = (name) => selectedFacility === 'all' ? visibleFacilities.includes(name) : selectedFacility === name;

    return (
      <div className={`p-6 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm flex flex-col gap-10`}>
        {/* View Mode Toggle */}
        <div className="flex justify-between items-center bg-gray-50 dark:bg-gray-700/30 p-4 rounded-xl border border-gray-100 dark:border-gray-700">
          <div>
            <h3 className={`text-md font-bold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Trend Visualization Mode</h3>
            <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Switch between aggregated totals and site-level comparisons.</p>
          </div>
          <div className="flex bg-white dark:bg-gray-800 rounded-lg p-1 border dark:border-gray-600 shadow-sm">
            <button 
              onClick={() => setTrendViewMode('aggregated')}
              className={`px-4 py-1.5 text-xs font-bold rounded-md transition-all ${trendViewMode === 'aggregated' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
            >
              Whole Org
            </button>
            <button 
              onClick={() => setTrendViewMode('comparison')}
              className={`px-4 py-1.5 text-xs font-bold rounded-md transition-all ${trendViewMode === 'comparison' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
            >
              Site Comparison
            </button>
          </div>
        </div>

        {/* Main Trend Chart */}
        {visibleCharts.includes('trends') && (
          <div className="mb-8">
            <h3 className={`text-lg font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {trendViewMode === 'aggregated' ? 'Historical Usage & Cost Trend' : 'Historical Site Metrics Comparison'}
            </h3>
            <div className="h-[450px] w-full">
              {renderTrendsChartBody()}
            </div>
          </div>
        )}

        {visibleCharts.includes('breakdown') && selectedFacility === 'all' && trendViewMode === 'aggregated' && (
          <div id="breakdown-chart-container" className={`border-t pt-8 ${darkMode ? 'border-gray-700' : 'border-gray-200'} bg-transparent mb-8`}>
            <div className="flex justify-between items-center mb-4">
              <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Portfolio Cost Contribution Breakdown</h3>
              <div className={`px-2 py-1 rounded bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-100 dark:border-indigo-800 flex items-center gap-2`}>
                <ArrowUpDown className="h-3 w-3 text-indigo-500" />
                <span className="text-[10px] font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-tighter">Sorted Bar Stacks: Smallest on Bottom</span>
              </div>
            </div>
            <div className="h-[400px] w-full">
              {renderBreakdownChartBody()}
            </div>
          </div>
        )}
...

        {visibleCharts.includes('carbon') && (
          <div className={`border-t pt-8 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <h3 className={`text-lg font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Carbon Emissions Profile</h3>
            <div className="h-[300px] w-full">
              {renderCarbonChartBody()}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderVarianceTab = () => {
    if (!varianceData) {
      return (
        <div className={`p-6 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm flex items-center justify-center h-64`}>
          <p className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Need at least 12 months of historical data to perform Year-Over-Year variance analysis.</p>
        </div>
      );
    }

    const { ltm, ptm, variance } = varianceData;
    
    const MetricCard = ({ title, current, prior, pct, prefix="", suffix="", inverseGood=true }) => {
      const isPositive = pct > 0;
      const isGood = inverseGood ? !isPositive : isPositive;
      
      return (
        <div className={`p-4 rounded-lg border ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
          <h4 className={`text-sm font-medium mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{title}</h4>
          <div className="flex flex-col gap-1">
            <div className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {prefix}{current.toLocaleString(undefined, {maximumFractionDigits: 0})}{suffix}
            </div>
            
            <div className="flex justify-between items-center mt-2">
              <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                Prior 12mo: {prefix}{prior.toLocaleString(undefined, {maximumFractionDigits: 0})}{suffix}
              </span>
              
              <span className={`flex items-center gap-1 font-bold text-sm px-2 py-1 rounded ${
                isGood 
                  ? (darkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700')
                  : (darkMode ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-700')
              }`}>
                {isPositive ? '+' : ''}{pct.toFixed(1)}%
              </span>
            </div>
          </div>
        </div>
      );
    };

    return (
      <div className={`p-6 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm flex flex-col gap-6`}>
        <div>
          <h3 className={`text-lg font-bold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Year-Over-Year Variance</h3>
          <p className={`text-sm mb-6 flex flex-col gap-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Comparing Last 12 Months (LTM) total to Prior 12 Months (PTM)
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard title="Total Spend" current={ltm.cost} prior={ptm.cost} pct={variance.cost} prefix="$" />
            <MetricCard title="Electric Usage" current={ltm.elec} prior={ptm.elec} pct={variance.elec} suffix=" kWh" />
            <MetricCard title="Natural Gas Usage" current={ltm.gas} prior={ptm.gas} pct={variance.gas} suffix=" CCF" />
            <MetricCard title="Carbon Footprint" current={ltm.carbon} prior={ptm.carbon} pct={variance.carbon} suffix=" mt" />
          </div>
        </div>

        {/* Can put additional sub-charts here if needed */}
      </div>
    );
  };

  const renderProjectionsTab = () => {
    if (projectionData.length === 0) {
      return (
        <div className={`p-6 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm flex items-center justify-center h-64`}>
          <p className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Need at least 12 months of historical data to generate reliable forecasts.</p>
        </div>
      );
    }
    
    // Merge the last 12 months of history with 12 months of forecast for visualization
    const historicalTail = chartData.slice(-12).map(d => ({...d, isHistory: true}));
    const forecastTail = projectionData.map(d => ({...d, isHistory: false}));
    const combinedData = [...historicalTail, ...forecastTail];

    return (
      <div className={`p-6 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm flex flex-col gap-6`}>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b pb-4 border-gray-200 dark:border-gray-700">
          <div>
            <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>12-Month Forward Projections</h3>
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Based on LTM moving averages</p>
          </div>
          
          {/* What-If Scenario Slider */}
          <div className={`p-4 rounded-lg border w-full sm:w-auto ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
            <label className={`block text-sm font-semibold mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
              What-If Scenario: Execute {whatIfReduction}% Reduction
            </label>
            <input 
              type="range" 
              min="0" 
              max="50" 
              step="5" 
              value={whatIfReduction} 
              onChange={(e) => setWhatIfReduction(Number(e.target.value))}
              className={`w-full h-2 rounded-lg appearance-none cursor-pointer ${darkMode ? 'bg-gray-600' : 'bg-gray-200'}`}
            />
            <div className="flex justify-between text-xs mt-1 text-gray-500">
              <span>Current Path (0%)</span>
              <span>-50%</span>
            </div>
          </div>
        </div>

        {visibleCharts.includes('projections') && (
          <div id="projections-chart-container" className="h-[400px] w-full mt-4 bg-transparent pt-4">
            {renderProjectionsChartBody()}
          </div>
        )}
      </div>
    );
  };

  const renderTargetsTab = () => (
    <div className={`flex flex-col lg:flex-row gap-6 h-full`}>
      {/* Target Setting Form */}
      <div className={`p-6 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm lg:w-1/3 flex-shrink-0`}>
        <h3 className={`text-lg font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'} flex items-center gap-2`}>
          <Target className="h-5 w-5 text-blue-500" />
          Set Reduction Target
        </h3>
        <form onSubmit={handleSaveTarget} className="space-y-4">
          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Scope</label>
            <select
              value={targetForm.facility}
              onChange={(e) => setTargetForm(prev => ({ ...prev, facility: e.target.value }))}
              className={`w-full py-2 px-3 rounded-md border text-sm focus:ring-2 focus:ring-blue-500 outline-none ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'}`}
            >
              <option value="all">Enterprise Portfolio</option>
              {facilityNames.map((f) => (
                <option key={f.id} value={f.name}>{f.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Utility Sector</label>
            <select
              value={targetForm.utility}
              onChange={(e) => setTargetForm(prev => ({ ...prev, utility: e.target.value }))}
              className={`w-full py-2 px-3 rounded-md border text-sm focus:ring-2 focus:ring-blue-500 outline-none ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'}`}
            >
              <option value="carbon">Total Carbon Emissions (CO2e)</option>
              <option value="electric">Electric (kWh)</option>
              <option value="gas">Natural Gas (CCF)</option>
            </select>
          </div>
          <div className="flex gap-4">
            <div className="flex-1">
              <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Type</label>
              <select
                value={targetForm.type}
                onChange={(e) => setTargetForm(prev => ({ ...prev, type: e.target.value }))}
                className={`w-full py-2 px-3 rounded-md border text-sm focus:ring-2 focus:ring-blue-500 outline-none ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'}`}
              >
                <option value="percentage">Percentage (%)</option>
                <option value="absolute">Absolute YoY</option>
              </select>
            </div>
            <div className="flex-1">
              <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Value</label>
              <input
                type="number"
                step="0.1"
                required
                value={targetForm.value}
                onChange={(e) => setTargetForm(prev => ({ ...prev, value: e.target.value }))}
                className={`w-full py-2 px-3 rounded-md border text-sm focus:ring-2 focus:ring-blue-500 outline-none ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'}`}
              />
            </div>
          </div>
          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Baseline Year</label>
            <input
              type="number"
              required
              min="2000"
              max="2100"
              value={targetForm.baselineYear}
              onChange={(e) => setTargetForm(prev => ({ ...prev, baselineYear: e.target.value }))}
              className={`w-full py-2 px-3 rounded-md border text-sm focus:ring-2 focus:ring-blue-500 outline-none ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'}`}
            />
          </div>
          <button
            type="submit"
            disabled={savingTarget}
            className={`w-full py-2 px-4 rounded-md text-white font-medium transition-colors mt-2 ${savingTarget ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'}`}
          >
            {savingTarget ? 'Saving...' : 'Save Target Limit'}
          </button>
        </form>
      </div>

      {/* Active Targets Overview */}
      <div className={`p-6 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm lg:w-2/3 flex-1 flex flex-col`}>
        <h3 className={`text-lg font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Active Enterprise Targets</h3>
        <div className="flex-1 overflow-y-auto pr-2">
          {allTargets.length === 0 ? (
            <div className={`h-full flex items-center justify-center border-2 border-dashed rounded-lg p-12 ${darkMode ? 'border-gray-700 text-gray-500' : 'border-gray-200 text-gray-400'}`}>
              <p>No reduction targets have been configured yet.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {allTargets.map((target, idx) => {
                // Calculate per-site allocations for 'all' percentage targets
                let allocations = null;
                if (target.facility === 'all' && target.targetType === 'percentage' && varianceData) {
                  const metricKey = target.utilityTargeted === 'carbon' ? 'carbon' : target.utilityTargeted === 'electric' ? 'elec' : 'gas';
                  allocations = facilityNames.map(f => {
                    const fData = facilityData[f.name];
                    if (!fData) return null;
                    const baselineVal = fData.months.slice(-12).reduce((sum, m) => sum + (m[target.utilityTargeted === 'carbon' ? 'carbon_emissions' : target.utilityTargeted === 'electric' ? 'electric_kwh' : 'gas_ccf'] || 0), 0);
                    const reductionAmount = baselineVal * (target.value / 100);
                    return { name: f.name, reduction: reductionAmount, baseline: baselineVal };
                  }).filter(a => a && a.reduction > 0).sort((a,b) => b.reduction - a.reduction);
                }

                return (
                  <div key={idx} className={`p-4 rounded-lg border ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {target.facility === 'all' ? 'Enterprise Portfolio' : target.facility}
                        </h4>
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'} capitalize`}>
                          {target.utilityTargeted} Reduction Goal
                        </p>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-sm font-bold ${darkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-800'}`}>
                        {target.targetType === 'percentage' ? `${target.value}%` : `-${target.value}`}
                      </div>
                    </div>
                    <div className={`text-xs mt-3 mb-2 flex justify-between ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      <span>Baseline Year: {target.baselineYear}</span>
                      <span>Set on: {new Date(target.createdAt).toLocaleDateString()}</span>
                    </div>
                    
                    {/* Per-site Allocation Preview */}
                    {allocations && allocations.length > 0 && (
                      <div className={`mt-4 pt-3 border-t ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                        <h5 className={`text-xs font-bold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Estimated Site Allocations (Based on LTM Baseline):</h5>
                        <div className="max-h-32 overflow-y-auto pr-2 flex flex-col gap-1.5 p-scroll">
                          {allocations.map(a => (
                            <div key={a.name} className="flex justify-between items-center">
                              <span className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{a.name}</span>
                              <span className={`text-[10px] font-medium ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                                -{a.reduction.toLocaleString(undefined, {maximumFractionDigits: 1})}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderA3ReportTab = () => {
    const handleFieldChange = (field, value) => {
      setA3Form(prev => ({ ...prev, [field]: value }));
    };

    return (
      <div className={`p-6 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm flex flex-col gap-6`}>
        <div className="flex justify-between items-center border-b pb-4 border-gray-200 dark:border-gray-700">
          <div>
            <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>A3 Problem Solving Report</h3>
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Draft your A3 narrative to tackle utility waste. Generates a structured A3-sized PDF.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <button 
                onClick={() => setShowHistory(!showHistory)}
                className={`flex items-center gap-2 px-3 py-2 border rounded-lg transition-colors ${darkMode ? 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'}`}
                title="View Draft History"
              >
                <History className="h-4 w-4" />
                {a3History.length > 0 && <span className="text-[10px] bg-indigo-500 text-white rounded-full px-1.5">{a3History.length}</span>}
              </button>
              
              {showHistory && (
                <div className={`absolute right-0 mt-2 w-64 rounded-xl shadow-2xl border z-50 overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                  <div className={`px-4 py-3 border-b text-xs font-bold uppercase tracking-wider ${darkMode ? 'bg-gray-900/50 text-gray-400' : 'bg-gray-50 text-gray-500'}`}>
                    Draft History
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {a3History.length === 0 ? (
                      <div className="p-4 text-center text-xs text-gray-500 italic">No previous versions</div>
                    ) : (
                      a3History.map((ver, i) => (
                        <button
                          key={i}
                          onClick={() => {
                            if (confirm('Restore this previous version? Current unsaved changes will be moved to history.')) {
                              setA3History(prev => [{ timestamp: new Date().toISOString(), data: {...a3Form} }, ...prev].slice(0, 10));
                              setA3Form(ver.data);
                              setShowHistory(false);
                            }
                          }}
                          className={`w-full px-4 py-3 text-left hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-colors border-b last:border-0 ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}
                        >
                          <div className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                            {new Date(ver.timestamp).toLocaleString()}
                          </div>
                          <div className="text-[10px] text-gray-500 truncate">{ver.data.title}</div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
            
            <button 
              onClick={() => {
                if (!a3Form.background || confirm('This will overwrite some of your current fields with AI-generated suggestions. Continue?')) {
                  handleAutoFillA3();
                }
              }}
              disabled={isAutoFilling}
              className={`flex items-center gap-2 px-4 py-2 font-medium rounded-lg shadow-sm transition-colors ${
                isAutoFilling 
                  ? 'bg-purple-400 text-white cursor-not-allowed' 
                  : 'bg-purple-600 hover:bg-purple-700 text-white'
              }`}
            >
              {isAutoFilling ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {isAutoFilling ? 'Drafting AI...' : 'Auto-Fill with AI'}
            </button>
            <button 
              onClick={generateA3Report}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg shadow-sm"
            >
              <Download className="h-5 w-5" />
              Generate PDF
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Left Side (PLAN) */}
          <div className="space-y-4">
             <h4 className={`font-semibold border-b pb-2 ${darkMode ? 'text-blue-400 border-gray-700' : 'text-blue-600 border-gray-200'}`}>PLAN</h4>
             
             <div>
               <label className={`block text-xs font-semibold mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Theme / Title</label>
               <input 
                 value={a3Form.title} onChange={e => handleFieldChange('title', e.target.value)}
                 className={`w-full p-2 rounded border text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-300'}`}
               />
             </div>
             
             <div>
               <label className={`block text-xs font-semibold mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>1. Background</label>
               <textarea rows={3} value={a3Form.background} onChange={e => handleFieldChange('background', e.target.value)} className={`w-full p-2 rounded border text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-300'}`} />
             </div>
             
             <div>
               <label className={`block text-xs font-semibold mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>2. Current Condition</label>
               <textarea rows={4} value={a3Form.currentCondition} onChange={e => handleFieldChange('currentCondition', e.target.value)} className={`w-full p-2 rounded border text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-300'}`} />
             </div>
             
             <div>
               <label className={`block text-xs font-semibold mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>3. Goals & Targets</label>
               <textarea rows={2} value={a3Form.goals} onChange={e => handleFieldChange('goals', e.target.value)} className={`w-full p-2 rounded border text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-300'}`} />
             </div>
             
             <div>
               <label className={`block text-xs font-semibold mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>4. Root Cause Analysis (5 Whys/Fishbone summary)</label>
               <textarea rows={4} value={a3Form.rootCause} onChange={e => handleFieldChange('rootCause', e.target.value)} className={`w-full p-2 rounded border text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-300'}`} />
             </div>
          </div>
          
          {/* Right Side (DO, CHECK, ACT) */}
          <div className="space-y-4">
             <h4 className={`font-semibold border-b pb-2 ${darkMode ? 'text-blue-400 border-gray-700' : 'text-blue-600 border-gray-200'}`}>DO, CHECK, ACT</h4>
             
             <div>
               <label className={`block text-xs font-semibold mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>5. Countermeasures (DO)</label>
               <textarea rows={4} value={a3Form.countermeasures} onChange={e => handleFieldChange('countermeasures', e.target.value)} className={`w-full p-2 rounded border text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-300'}`} />
             </div>
             
             <div>
               <label className={`block text-xs font-semibold mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>6. Implementation Plan (DO)</label>
               <textarea rows={4} value={a3Form.implementationPlan} onChange={e => handleFieldChange('implementationPlan', e.target.value)} className={`w-full p-2 rounded border text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-300'}`} />
             </div>
             
             <div>
               <label className={`block text-xs font-semibold mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>7. Verification & Follow-up (CHECK / ACT)</label>
               <textarea rows={4} value={a3Form.followUp} onChange={e => handleFieldChange('followUp', e.target.value)} className={`w-full p-2 rounded border text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-300'}`} />
             </div>

             {/* Visual Attachments Toggle Area */}
             <div className={`mt-6 p-4 rounded-lg border ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
               <h4 className={`text-sm font-bold mb-3 ${darkMode ? 'text-white' : 'text-gray-800'} flex items-center gap-2`}>
                 <BarChart2 className="w-4 h-4 text-blue-500"/> Appendix: Include Dashboard Visuals
               </h4>
               <div className="flex flex-col gap-2">
                 <label className={`flex items-center gap-2 text-sm cursor-pointer hover:opacity-80 transition-opacity ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                   <input type="checkbox" checked={a3Form.includeTrendsChart} onChange={(e) => handleFieldChange('includeTrendsChart', e.target.checked)} className="rounded text-blue-600 focus:ring-blue-500 bg-gray-100 border-gray-300" />
                   Historical Usage & Cost Trends Chart
                 </label>
                 <label className={`flex items-center gap-2 text-sm cursor-pointer hover:opacity-80 transition-opacity ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                   <input type="checkbox" checked={a3Form.includeBreakdownChart} onChange={(e) => handleFieldChange('includeBreakdownChart', e.target.checked)} className="rounded text-blue-600 focus:ring-blue-500 bg-gray-100 border-gray-300" />
                   Portfolio Contribution Breakdown
                 </label>
                 <label className={`flex items-center gap-2 text-sm cursor-pointer hover:opacity-80 transition-opacity ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                   <input type="checkbox" checked={a3Form.includeProjectionsChart} onChange={(e) => handleFieldChange('includeProjectionsChart', e.target.checked)} className="rounded text-blue-600 focus:ring-blue-500 bg-gray-100 border-gray-300" />
                   Forecasting & Projections Chart
                 </label>
                 <label className={`flex items-center gap-2 text-sm cursor-pointer hover:opacity-80 transition-opacity ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                   <input type="checkbox" checked={a3Form.includeCarbonChart} onChange={(e) => handleFieldChange('includeCarbonChart', e.target.checked)} className="rounded text-blue-600 focus:ring-blue-500 bg-gray-100 border-gray-300" />
                   Carbon Emissions Profile Chart
                 </label>
                 <label className={`flex items-center gap-2 text-sm cursor-pointer hover:opacity-80 transition-opacity ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                   <input type="checkbox" checked={a3Form.includeCostSavings} onChange={(e) => handleFieldChange('includeCostSavings', e.target.checked)} className="rounded text-blue-600 focus:ring-blue-500 bg-gray-100 border-gray-300" />
                   Potential Cost Savings Breakdown
                 </label>
               </div>
                </div>
              </div>

              {/* Conditional Chart Rendering for A3 Appendices */}
              {a3Form.includeTrendsChart && (
                <div id="a3-trends-chart" className={`mt-8 border-t pt-4 ${darkMode ? 'border-gray-700' : 'border-gray-200'} bg-transparent`}>
                  <h4 className={`max-w-[700px] text-sm font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Historical Usage & Cost Trends</h4>
                  <div className="h-[450px] w-full bg-white dark:bg-gray-800 shrink-0">{renderTrendsChartBody()}</div>
                </div>
              )}
              {a3Form.includeBreakdownChart && (
                <div id="a3-breakdown-chart" className={`mt-8 border-t pt-4 ${darkMode ? 'border-gray-700' : 'border-gray-200'} bg-transparent`}>
                  <h4 className={`max-w-[700px] text-sm font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Portfolio Cost Contribution Breakdown</h4>
                  <div className="h-[400px] w-full bg-white dark:bg-gray-800 shrink-0">{renderBreakdownChartBody()}</div>
                </div>
              )}
              {a3Form.includeProjectionsChart && (
                <div id="a3-projections-chart" className={`mt-8 border-t pt-4 ${darkMode ? 'border-gray-700' : 'border-gray-200'} bg-transparent`}>
                  <h4 className={`max-w-[700px] text-sm font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Forecasting & Target Projections</h4>
                  <div className="h-[400px] w-full bg-white dark:bg-gray-800 shrink-0">{renderProjectionsChartBody()}</div>
                </div>
              )}
              {a3Form.includeCarbonChart && (
                <div id="a3-carbon-chart" className={`mt-8 border-t pt-4 ${darkMode ? 'border-gray-700' : 'border-gray-200'} bg-transparent`}>
                  <h4 className={`max-w-[700px] text-sm font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Carbon Emissions Profile</h4>
                  <div className="h-[300px] w-full bg-white dark:bg-gray-800 shrink-0">
                    {renderCarbonChartBody()}
                  </div>
                </div>
              )}
           </div>
        </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-black/60 backdrop-blur-sm">
      <div className={`w-full max-w-7xl h-[90vh] flex flex-col rounded-xl shadow-2xl overflow-hidden ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        
        {/* Unified App Header: Title + Global Controls + Tabs */}
        <div className={`flex flex-col gap-3 px-4 sm:px-6 pt-4 border-b ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shrink-0 z-10 relative`}>
          
          <div className="flex flex-wrap items-start justify-between gap-4">
            {/* Title Section */}
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${darkMode ? 'bg-blue-900/30' : 'bg-blue-100'} hidden sm:block`}>
                <Layers className={`h-6 w-6 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
              </div>
              <div>
                <h2 className={`text-lg sm:text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} leading-tight`}>
                  Enterprise Analytics & A3 Reporting
                </h2>
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Deep-dive problem solving and target tracking
                </p>
              </div>
            </div>

            {/* Global Controls & Close */}
            <div className="flex flex-wrap items-center gap-2 flex-1 justify-end">
              <select
                value={selectedFacility}
                onChange={(e) => setSelectedFacility(e.target.value)}
                className={`py-1.5 px-3 pl-8 rounded border text-xs sm:text-sm font-medium appearance-none focus:ring-2 focus:ring-blue-500 outline-none ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-700'
                }`}
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='${darkMode ? '%239CA3AF' : '%236B7280'}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z'/%3E%3Ccircle cx='12' cy='10' r='3'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'left 0.5rem center',
                  backgroundSize: '1rem',
                }}
              >
                <option value="all">Enterprise Portfolio</option>
                {facilityNames.map((f) => (
                  <option key={f.id} value={f.name}>{f.name}</option>
                ))}
              </select>

              <div className="hidden sm:flex items-center rounded border overflow-hidden">
                {['1yr', '2yr', '3yr', '5yr', 'all'].map((range) => (
                  <button
                    key={range}
                    onClick={() => setTimeRange(range)}
                    className={`px-3 py-1.5 text-xs font-medium border-x focus:z-10 first:border-l-0 last:border-r-0 ${
                      timeRange === range
                        ? (darkMode ? 'bg-blue-600 text-white border-blue-600' : 'bg-blue-100 text-blue-700 border-blue-200')
                        : (darkMode ? 'bg-gray-800 text-gray-300 border-gray-700 hover:bg-gray-700' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50')
                    }`}
                  >
                    {range.toUpperCase()}
                  </button>
                ))}
              </div>
              
              <button 
                onClick={loadData}
                disabled={loading}
                className={`flex items-center gap-2 px-3 py-1.5 text-xs sm:text-sm font-medium rounded border transition-colors ${
                  darkMode 
                    ? 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700' 
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Activity className="h-3.5 w-3.5" />
                Refresh
              </button>

              <button 
                onClick={() => setAiChatOpen(!aiChatOpen)}
                className={`flex items-center gap-2 px-3 py-1.5 text-xs sm:text-sm font-medium rounded border transition-colors ${
                  aiChatOpen
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/40 dark:border-indigo-700 dark:text-indigo-300'
                    : darkMode 
                      ? 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700' 
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <MessageSquare className="h-3.5 w-3.5" />
                Ask AI
              </button>

              <div className="relative">
                <button 
                  onClick={() => setShowDashboardCustomizer(!showDashboardCustomizer)}
                  className={`flex items-center gap-2 px-3 py-1.5 text-xs sm:text-sm font-medium rounded border transition-colors ${
                    showDashboardCustomizer
                      ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/40 dark:border-blue-700 dark:text-blue-300'
                      : darkMode 
                        ? 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700' 
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Settings className="h-3.5 w-3.5" />
                  Options
                </button>
                
                {showDashboardCustomizer && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowDashboardCustomizer(false)} />
                    <div className={`absolute right-0 mt-2 w-56 z-50 p-3 rounded-xl shadow-2xl border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200 animate-in fade-in slide-in-from-top-2 duration-200'}`}>
                      <h4 className={`text-[10px] font-bold uppercase tracking-wider mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Visible Charts</h4>
                      <div className="space-y-1">
                        {[
                          { id: 'trends', label: 'Trends & Performance' },
                          { id: 'breakdown', label: 'Portfolio Breakdown' },
                          { id: 'projections', label: 'Savings Projections' },
                          { id: 'carbon', label: 'Carbon Emissions' }
                        ].map(chart => (
                          <label key={chart.id} className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors ${darkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'}`}>
                            <span className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{chart.label}</span>
                            <input 
                              type="checkbox"
                              checked={visibleCharts.includes(chart.id)}
                              onChange={() => {
                                if (visibleCharts.includes(chart.id)) {
                                  if (visibleCharts.length > 1) setVisibleCharts(visibleCharts.filter(id => id !== chart.id));
                                } else {
                                  setVisibleCharts([...visibleCharts, chart.id]);
                                }
                              }}
                              className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                          </label>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>

              <button 
                onClick={onClose}
                className={`p-1.5 rounded transition-colors ml-2 ${darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="flex space-x-1 overflow-x-auto no-scrollbar border-t-0 mt-1">
            {tabs.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    isActive
                      ? (darkMode ? 'border-blue-500 text-blue-400' : 'border-blue-600 text-blue-700')
                      : (darkMode ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300')
                  }`}
                >
                  {tab.icon}
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Site Filter Tags (Stays near top but separate from sticky header) */}
        {selectedFacility === 'all' && !loading && !error && (
          <div className={`px-4 sm:px-6 py-2 border-b flex flex-col gap-1.5 shrink-0 transition-all duration-300 ${darkMode ? 'border-gray-700 bg-gray-900/40' : 'border-gray-200 bg-gray-50/60'}`}>
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => setShowFilters(!showFilters)}
                  className={`p-1 rounded-md transition-colors ${darkMode ? 'hover:bg-gray-700 text-gray-500' : 'hover:bg-gray-200 text-gray-400'}`}
                >
                  {showFilters ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                </button>
                <span className={`text-[10px] font-bold uppercase tracking-wider ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    Portfolio Visibility: {visibleFacilities.length} of {facilityNames.length} Sites Included
                </span>
              </div>
              {showFilters && (
                <div className="flex gap-3">
                  <button onClick={() => setVisibleFacilities(facilityNames.map(f => f.name))} className="text-[10px] font-bold text-blue-500 hover:text-blue-400 uppercase">Select All</button>
                  <button onClick={() => setVisibleFacilities([])} className="text-[10px] font-bold text-gray-500 hover:text-gray-400 uppercase">Clear All</button>
                </div>
              )}
            </div>
            
            {showFilters && (
              <div className="flex flex-wrap gap-1.5 py-1 animate-in fade-in slide-in-from-top-1 duration-200">
                {facilityNames.map(f => (
                  <button
                    key={f.id}
                    onClick={() => {
                      setVisibleFacilities(prev => 
                        prev.includes(f.name) ? prev.filter(name => name !== f.name) : [...prev, f.name]
                      );
                    }}
                    className={`px-2 py-0.5 rounded text-[10px] font-bold transition-all border ${
                      visibleFacilities.includes(f.name)
                        ? (darkMode ? 'bg-blue-600/20 border-blue-500/50 text-blue-400 shadow-[0_0_8px_rgba(59,130,246,0.2)]' : 'bg-blue-100 border-blue-200 text-blue-800')
                        : (darkMode ? 'bg-gray-800 border-gray-700 text-gray-500 hover:border-gray-600' : 'bg-gray-50 border-gray-200 text-gray-400 hover:text-gray-600')
                    }`}
                  >
                    {f.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 p-scroll relative overflow-x-hidden">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-full space-y-4">
              <Loader2 className={`h-12 w-12 animate-spin ${darkMode ? 'text-blue-500' : 'text-blue-600'}`} />
              <p className={`text-lg font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Processing report...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-full space-y-4">
              <div className={`p-4 rounded-full ${darkMode ? 'bg-red-900/30 text-red-500' : 'bg-red-100 text-red-600'}`}>
                <AlertTriangle className="h-12 w-12" />
              </div>
              <p className={`text-lg font-medium ${darkMode ? 'text-red-400' : 'text-red-600'}`}>{error}</p>
              <button 
                onClick={loadData}
                className={`px-4 py-2 rounded-lg font-medium ${darkMode ? 'bg-gray-800 text-white hover:bg-gray-700' : 'bg-white border text-gray-700 hover:bg-gray-50'}`}
              >
                Try Again
              </button>
            </div>
          ) : (
            <div className="h-full relative w-full">
              <div className={activeTab === 'trends' ? 'block h-full' : 'absolute top-[-9999px] left-[-9999px] w-full min-w-[1000px]'}>
                {renderTrendsTab()}
              </div>
              <div className={activeTab === 'variance' ? 'block h-full' : 'absolute top-[-9999px] left-[-9999px] w-full min-w-[1000px]'}>
                {renderVarianceTab()}
              </div>
              <div className={activeTab === 'projections' ? 'block h-full' : 'absolute top-[-9999px] left-[-9999px] w-full min-w-[1000px]'}>
                {renderProjectionsTab()}
              </div>
              <div className={activeTab === 'targets' ? 'block h-full' : 'absolute top-[-9999px] left-[-9999px] w-full min-w-[1000px]'}>
                {renderTargetsTab()}
              </div>
              <div className={activeTab === 'report' ? 'block h-full' : 'absolute top-[-9999px] left-[-9999px] w-full min-w-[1000px]'}>
                {renderA3ReportTab()}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Floating AI Analytics Panel - isolated component for performance */}
      <AIAnalyticsPanel 
        isOpen={aiChatOpen}
        onClose={() => setAiChatOpen(false)}
        darkMode={darkMode}
        selectedFacility={selectedFacility}
        visibleFacilities={visibleFacilities}
        chartData={chartData}
        allTargets={allTargets}
      />
    </div>
  );
};

export default EnterpriseAnalytics;
